import SvgIcon from 'components/helpers/SvgIcon/SvgIcon';
import { useCallback, useEffect, useState } from 'react';
import { useMenu, useClearRefinements, useCurrentRefinements } from 'react-instantsearch-hooks-web';
import { classnames } from 'tailwindcss-classnames';
import { DiseaseProps, disease } from 'components/helpers/NmdpSite/TCDirectorySearch/_types';
import { useSearchDictionary } from 'components/helpers/AlgoliaSearch/util';

const DiseaseFacet = ({ onTxIdChange, diseases }: DiseaseProps) => {
  const dict = useSearchDictionary();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [searchKey, setSearchKey] = useState<string>('');
  const [searchElement, setSearchElement] = useState<string>('');
  const { items } = useCurrentRefinements({
    includedAttributes: ['diseases'],
  });

  const diseaseFilter = useMenu({
    attribute: 'diseases',
  });
  const { refine } = useClearRefinements({
    includedAttributes: ['diseases'],
  });

  const filterFunction = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setIsOpen(true);
      setSearchKey(event.currentTarget.value);
      setSearchElement(event.currentTarget.value);
      if (event.currentTarget.value === '') {
        refine();
        onTxIdChange('');
      }
    },
    [refine, onTxIdChange, setSearchKey]
  );
  const handleTypeSelect = useCallback(
    (facet: disease) => {
      if (isOpen) {
        setIsOpen(false);
      }
      setSearchElement(facet.value);
      diseaseFilter.refine(facet.key);
      onTxIdChange(facet.key);
    },
    [isOpen, diseaseFilter, onTxIdChange]
  );

  useEffect(() => {
    if (!(items.length > 0)) {
      onTxIdChange('');
      setSearchElement('');
      setSearchKey('');
    }
  }, [items, onTxIdChange]);
  const getValue = (key: string) => {
    const found = diseases?.find((item) => item.key === key);
    if (found) return found.value;
    return key;
  };

  return (
    <div className={classnames('my-4', 'relative', 'md:mr-10', 'md:basis-5/12')}>
      <div className={classnames('mb-0.5')}>
        <span className={classnames('text-solidBlack', 'text-base', 'font-semibold', 'mr-1')}>
          {dict.diagnosis}
        </span>
        <span className={classnames('text-gray-darkGray', 'text-base', 'font-regular')}>
          {dict.optional}
        </span>
      </div>
      <details
        onToggle={(e) => setIsOpen((e.currentTarget as HTMLDetailsElement).open)}
        open={isOpen}
      >
        <summary
          className={classnames(
            'relative',
            'bg-white',
            'border',
            'border-gray-spunPearl',
            'rounded-md',
            'w-full',
            'flex',
            'items-center',
            'justify-between'
          )}
        >
          <input
            type="search"
            placeholder="Enter diagnosis.."
            className={classnames('tx-input', 'outline-none', 'rounded-md', 'p-1.5', 'w-full')}
            onChange={(e) => filterFunction(e)}
            value={searchElement}
          />
          <span className={classnames('p-2')}>
            <SvgIcon
              icon="chevron-down"
              size="xs"
              viewBoxSize="0 0 24 24"
              className={classnames('mr-2')}
            />
          </span>
        </summary>
        <div
          className={classnames(
            'absolute',
            'z-20',
            'p-0.5',
            'bg-white',
            'border',
            'border-gray-spunPearl',
            'rounded-md',
            'w-full',
            'max-h-60',
            'overflow-y-scroll'
          )}
        >
          {diseases?.map((item) => {
            return (
              (searchKey === '' ||
                getValue(item.value).toLowerCase().includes(searchKey.toLowerCase())) && (
                <option
                  className={classnames(
                    'whitespace-pre-line',
                    'cursor-pointer',
                    'hover:text-white',
                    'hover:bg-blue',
                    'p-1'
                  )}
                  key={item.key}
                  value={item.key}
                  onClick={() => handleTypeSelect(item)}
                >
                  {item.value}
                </option>
              )
            );
          })}
        </div>
      </details>
      <div className={classnames('text-gray-darkGray', 'text-sm', 'font-regular', 'p-1')}>
        Use drop-down menu or begin typing name of disease
      </div>
    </div>
  );
};

export default DiseaseFacet;
